// Weekly schedule helpers (HR revamp Batch 2, owner decision "Scheduling Option B"):
// each weekday independently selects one shift or Rest. The API speaks ISO weekdays,
// 1 = Monday … 7 = Sunday; a week is { 1..7 → shiftId | null }.

export const WEEKDAYS = [
  { day: 1, short: "Mon", long: "Monday" },
  { day: 2, short: "Tue", long: "Tuesday" },
  { day: 3, short: "Wed", long: "Wednesday" },
  { day: 4, short: "Thu", long: "Thursday" },
  { day: 5, short: "Fri", long: "Friday" },
  { day: 6, short: "Sat", long: "Saturday" },
  { day: 7, short: "Sun", long: "Sunday" },
];

/** The chosen shift on the given work weekdays, Rest on the others. */
export function defaultWeek(shiftId, workWeekdays = [1, 2, 3, 4, 5]) {
  const on = new Set((workWeekdays || []).map(Number));
  return Object.fromEntries(WEEKDAYS.map(({ day }) => [day, on.has(day) && shiftId ? Number(shiftId) : null]));
}

/** An assignment row's week, from its weekly_schedule (or its shift every day when none is stored). */
export function weekFromAssignment(assignment) {
  const rows = assignment?.weekly_schedule;
  if (Array.isArray(rows) && rows.length === 7) {
    return Object.fromEntries(rows.map((row) => [Number(row.weekday), row.shift_id ? Number(row.shift_id) : null]));
  }
  return defaultWeek(assignment?.shift_id, [1, 2, 3, 4, 5, 6, 7]);
}

export function sameWeek(a = {}, b = {}) {
  return WEEKDAYS.every(({ day }) => (a[day] ?? null) === (b[day] ?? null));
}

export function workDayCount(week = {}) {
  return WEEKDAYS.filter(({ day }) => week[day]).length;
}

function dayRange(days) {
  const first = WEEKDAYS[days[0] - 1].short;
  const last = WEEKDAYS[days[days.length - 1] - 1].short;
  if (days.length === 1) return first;
  if (days.length === 2) return `${first}, ${last}`;
  return `${first}–${last}`;
}

/**
 * Plain-words summary: "Every day: Morning", "Mon–Fri Morning · Rest: Sat, Sun",
 * "Mon, Tue Morning · Wed, Thu Afternoon · Fri Night · Sat Morning · Rest: Sun".
 * nameOf(shiftId) gives the shift's name.
 */
export function describeWeek(week = {}, nameOf = (id) => `Shift #${id}`) {
  const groups = [];
  for (const { day } of WEEKDAYS) {
    const id = week[day] ?? null;
    const last = groups[groups.length - 1];
    if (last && last.id === id && last.days[last.days.length - 1] === day - 1) last.days.push(day);
    else groups.push({ id, days: [day] });
  }
  const working = groups.filter((group) => group.id);
  const rest = WEEKDAYS.filter(({ day }) => !week[day]).map(({ short }) => short);
  if (!working.length) return "No work days";
  if (!rest.length && working.length === 1) return `Every day: ${nameOf(working[0].id)}`;
  const parts = working.map((group) => `${dayRange(group.days)} ${nameOf(group.id)}`);
  return rest.length ? `${parts.join(" · ")} · Rest: ${rest.join(", ")}` : parts.join(" · ");
}
